<template>
	<div role="main" id="main-content" tabindex="0">
		<div id="yearEndDistribution">
			<div class="yed-hero">
				<div class="container">
					<Breadcrumbs :breadcrumbs="breadcrumbs" />
				</div>
				<div class="box">
					<div class="col">
						<Hero
							:imageSrc="heroContent.imageSrc"
							:showPrintButton="heroContent.showPrintButton"
							:showShareButton="heroContent.showShareButton"
							:showBgOverlay="heroContent.showBgOverlay"
							:customBgOverlay="heroContent.customBgOverlay"
							:showGlowEffect="heroContent.showGlowEffect"
							:showShadowEffect="heroContent.showShadowEffect"
							:customShadowRGBColor="heroContent.customShadowRGBColor"
							:invertColorsLeft="heroContent.invertColorsLeft"
							:invertColorsRight="heroContent.invertColorsRight"
							:eyebrow="heroContent.eyebrow"
							:headline="heroTitle"
							:excerpt="heroContent.excerpt"
							:link="heroContent.ctaLink"
							:subtopicNav="heroContent.subtopicNav"
							:pullLeft="heroContent.pullLeft"
							:pullRight="heroContent.pullRight"
							:customGlowRGBColor="heroContent.customGlowRGBColor"
							:customGlowAlphaValue="heroContent.customGlowAlphaValue"
							:customShadowAlphaValue="heroContent.customShadowAlphaValue"
						/>
					</div>
				</div>
			</div>
			<section class="preliminary-distribution-section" v-if="showPreliminaryDistribution">
				<div class="container">
					<PDFDownload :pdfs="pdfDownloadData">
						<template slot="pdf-title-0">
							<h2 class="h2" v-html="this.preliminaryDistributionContent.title" />
						</template>
					</PDFDownload>
				</div>
			</section>
			<section class="year-end-distributions-section">
				<div class="container">
					<h2 class="h2" v-html="tableTitle" />
					<ErrorState v-if="showTableError" color="white" />
					<div class="col" v-else>
						<div class="box">
							<TableSearch
								searchId="yed-search"
								:tableData="fundTableData"
								@updateFilteredTableRows="filterTable"
								@updateSearchErrorState="updateSearchErrorState"
							/>
							<download-button
								class="lg-3 md-4 sm-16"
								:title="downloadContent.buttonText"
								:buttonText="downloadContent.buttonText"
								@downloadClicked="downloadYEDistributions"
								v-if="!showSearchError"
							/>
						</div>
						<div v-if="showSearchError" class="search-error">
							<span v-html="searchContent.error.beforeSearchInput"></span>"{{ searchInput }}"<span
								v-html="searchContent.error.afterSearchInput"
							></span>
						</div>
						<div v-else>
							<year-end-distributions-table
								v-if="filteredTableData.length > 0"
								:fundData="filteredTableData"
								:tableHeaderContent="tableHeaderContent"
							/>
							<div class="descriptions">
								<p class="p2">
									<span v-html="descriptionsContent.distributions.descriptionBeforeLabel" />
									<Tooltip
										:text="descriptionsContent.distributions.label"
										:message="descriptionsContent.distributions.definition"
										:label-classes="['distribution-label']"
									/>
									<span v-html="descriptionsContent.distributions.descriptionAfterLabel" />
								</p>
								<p class="p2">
									<button
										class="modal-link"
										@click="openImportantFundPerformanceInformation"
										v-html="this.descriptionsContent.importantFundPerformanceInformation.label"
									></button>
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>
	</div>
</template>

<script>
import axios from "axios";
import Hero from "@vg-faset/ui/src/components/hero/Hero.vue";
import Tooltip from "@vg-faset/ui/src/components/tooltip/Tooltip";
import { EventBus } from "@vg-faset/core";
import ImportantFundPerformanceInformation from "./ImportantFundPerformanceInformation.vue";
import YearEndDistributionsTable from "./YearEndDistributionsTable.vue";
import adobeLaunchService from "../services/adobeLaunchService.js";
import { createDownload, tripleDashNull } from "@/services";
import papa from "papaparse";
import { formattedDate, toUSD, append } from "@vg-faset/core/js/filters/string/index";
import TableSearch from "@/common/TableSearch.vue";
import DownloadButton from "@/common/DownloadButton.vue";
import { Breadcrumbs } from "@vg-faset/ui/src/components/breadcrumbs";
import PDFDownload from "@vg-faset/ui/src/components/pdf-download/PDFDownload";
import ErrorState from "@vg-faset/ui/src/components/error-state/ErrorState";
import { utilityService } from "@/services/utility.service.js";

export default {
	name: "YearEndDistributions",
	components: {
		Hero,
		Tooltip,
		YearEndDistributionsTable,
		TableSearch,
		DownloadButton,
		Breadcrumbs,
		PDFDownload,
		ErrorState
	},
	data() {
		return {
			yearEndDistributions: [],
			searchInput: "",
			filteredTableData: [],
			adobeTracking: {
				siteSection: "funds-tax",
				subPage: "year-end-vanguard-funds-distributions",
				is404: "N"
			},
			secYieldFootnotesString: "",
			asOfYear: "",
			showSearchError: false,
			showTableError: true
		};
	},
	created() {
		axios
			.get("fundData/yearEndDistributions")
			.then((results) => {
				if (results.data.length > 0) {
					this.yearEndDistributions = results.data;
					this.asOfYear = results.data[0].asOfYearEnd;
					this.filterTable(this.fundTableData);
					this.setSecYieldFootnotesString();
					this.showTableError = false;
				}
			})
			.catch({});

		setTimeout(() => {
			adobeLaunchService.trackPageLoad(
				this.adobeTracking.is404,
				this.adobeTracking.siteSection,
				this.adobeTracking.subPage
			);
		}, 0);
	},
	computed: {
		content() {
			return this.$store.state.content;
		},
		heroContent() {
			return this.content.heroSection;
		},
		heroTitle() {
			return this.heroContent.headline;
		},
		searchContent() {
			return this.content.search;
		},
		downloadContent() {
			return this.content.download;
		},
		tableHeaderContent() {
			return this.content.tableHeader;
		},
		descriptionsContent() {
			return this.content.descriptions;
		},
		fundTableData() {
			return this.yearEndDistributions;
		},
		breadcrumbsContent() {
			return this.content.breadcrumbs;
		},
		breadcrumbs() {
			return [
				{
					text: this.breadcrumbsContent[0].text,
					url: "/tax-center"
				},
				{
					text: this.breadcrumbsContent[1].text
				}
			];
		},
		tableTitle() {
			return this.asOfYear ? `${this.asOfYear} ${this.content.tableTitle}` : this.content.tableTitle;
		},
		preliminaryDistributionContent() {
			return this.content.preliminaryDistribution;
		},
		pdfDownloadData() {
			let pdfs = [
				{
					title: this.preliminaryDistributionContent.title,
					description: this.preliminaryDistributionContent.description,
					url: this.preliminaryDistributionContent.buttonUrl,
					anchorText: this.preliminaryDistributionContent.buttonText,
					anchorLabel: this.preliminaryDistributionContent.buttonText,
					anchorTarget: "_blank"
				}
			];
			return pdfs;
		},
		showPreliminaryDistribution() {
			return this.preliminaryDistributionContent && this.preliminaryDistributionContent.buttonUrl;
		}
	},
	methods: {
		openImportantFundPerformanceInformation(e) {
			EventBus.$emit("modal:set", ImportantFundPerformanceInformation);
			EventBus.$emit("modal:set-id", "important-fund-performance-information");
			EventBus.$emit("modal:show", e);
		},
		filterTable: function (filteredRows) {
			this.filteredTableData = filteredRows;
		},
		updateSearchErrorState: function (shouldShowSearchError, searchInput) {
			this.showSearchError = shouldShowSearchError;
			this.searchInput = searchInput;
		},
		downloadYEDistributions: function () {
			let csvFileData = [];
			const fileNameWithYear = `${this.asOfYear} ${this.downloadContent.fileName}`;

			const csvTitle = `${this.asOfYear} ${this.downloadContent.title}\n\n`;
			csvFileData.push(csvTitle);

			csvFileData.push(`"${this.downloadContent.footnotes.excelNote}"\n\n`);

			const csvFundData = papa.unparse(this.getDownloadData());
			csvFileData.push(csvFundData);
			csvFileData.push(this.getDownloadFootnotes());

			createDownload(fileNameWithYear, csvFileData);
		},
		getDownloadData: function () {
			let downloadData = {
				fields: this.getDownloadHeaders(),
				data: []
			};

			for (const fund of this.filteredTableData) {
				let fundData = [];

				fundData.push(tripleDashNull(fund.name));
				fundData.push(tripleDashNull(fund.ticker));
				fundData.push(tripleDashNull(fund.distributionType));
				fundData.push(tripleDashNull(toUSD(fund.mostRecentDistribution, 5)));
				fundData.push(tripleDashNull(formattedDate(fund.exDividendDate)));
				fundData.push(tripleDashNull(formattedDate(fund.recordDate)));
				fundData.push(tripleDashNull(formattedDate(fund.reinvestDate)));
				fundData.push(tripleDashNull(formattedDate(fund.payableDate)));
				fundData.push(tripleDashNull(toUSD(fund.reinvestPrice)));
				fundData.push(tripleDashNull(append(utilityService.toFixed(fund.distributionYield, 2), "%")));
				fundData.push(tripleDashNull(this.getSecYield(fund)));

				downloadData.data.push(fundData);
			}
			return downloadData;
		},
		getDownloadHeaders: function () {
			let labels = this.tableHeaderContent.labels;
			let headers = [];
			headers.push(labels.fundName);
			headers.push(labels.symbol);
			headers.push(labels.distributionType);
			headers.push(labels.mostRecentDistribution);
			headers.push(labels.exDividendDate);
			headers.push(labels.recordDate);
			headers.push(labels.reinvestDate);
			headers.push(labels.payableDate);
			headers.push(labels.reinvestPrice);
			headers.push(labels.distributionYield);
			headers.push(labels.secYield);

			return headers;
		},
		getSecYield: function (fund) {
			let secYield;

			if (fund.secYield && fund.secYieldFootnote) {
				secYield = `${append(utilityService.toFixed(fund.secYield, 2), "%")} ${fund.secYieldFootnote.code}`;

				if (fund.secYieldFootnote2) {
					secYield += `,${fund.secYieldFootnote2.code}`;
				}
			}
			return secYield;
		},
		getDownloadFootnotes: function () {
			let footnotes = [];

			footnotes.push(`"${this.downloadContent.footnotes.pastPerformance}"`);
			footnotes.push(`"${this.downloadContent.footnotes.forMoreInfo}"`);
			footnotes.push(`"${this.downloadContent.footnotes.secYieldAsOf}"`);

			footnotes.push(this.secYieldFootnotesString);

			if (this.isMoneyMarketInDownload()) {
				footnotes.push(`"${this.downloadContent.footnotes.moneyMarket}"`);
			}

			footnotes.push(`"${this.downloadContent.footnotes.etfShares}"`);

			return `\n\n${footnotes.join("\n\n")}`;
		},
		isMoneyMarketInDownload: function () {
			return this.filteredTableData.some((distribution) => distribution.isMoneyMarket === true);
		},
		setSecYieldFootnotesString: function () {
			let uniqueSecFootnotes = new Set();

			this.yearEndDistributions.forEach((distribution) => {
				if (distribution.secYieldFootnote) {
					uniqueSecFootnotes.add(
						`"${distribution.secYieldFootnote.code} ${distribution.secYieldFootnote.description}"`
					);
				}

				if (distribution.secYieldFootnote2) {
					uniqueSecFootnotes.add(
						`"${distribution.secYieldFootnote2.code} ${distribution.secYieldFootnote2.description}"`
					);
				}
			});

			this.secYieldFootnotesString = [...uniqueSecFootnotes].sort().join("\n");
		}
	}
};
</script>

<style lang="scss">
#yearEndDistribution {
	.container {
		@include to-lg {
			padding-bottom: 28px;
		}
		padding-bottom: 48px;
	}

	.hero-eyebrow {
		a {
			color: $color--white;
			text-decoration: underline;
		}
	}

	.yed-hero {
		.container {
			padding-bottom: 0px;
		}

		@include from-md {
			.hero {
				max-height: 262px;

				.hero-text h1 {
					white-space: nowrap;
					margin: 35px 0 24px;
				}
			}
		}
	}

	.breadcrumbs {
		padding-bottom: 18px;
		padding-top: 18px;
	}

	.descriptions {
		.modal-link {
			color: $color--red;
			text-decoration: underline;
			cursor: pointer;
			margin: 0;
			padding: 0;
			border: 0;
			background: transparent;
			font-family: inherit;
			font-size: inherit;
		}

		.distribution-label {
			color: $color--red;
		}

		.tooltip-container {
			color: $color--almost-black;
		}
	}

	.search-error {
		font-size: 24px;
		padding-bottom: 175px;
	}

	.search-input {
		margin-bottom: 4px;
	}

	.preliminary-distribution-section {
		background-color: $color--neutral-9;
		@include to-lg {
			padding-top: 28px;
		}
		padding-top: 48px;

		.icon-right::before {
			content: "\e910";
		}

		.pdf-download {
			&__item-header {
				margin-bottom: 24px;
			}

			&__item-description {
				margin-bottom: 24px;

				@include from-md {
					margin-bottom: 40px;
				}
			}

			&__item-thumbnail {
				margin-right: 17%;
				display: flex;
				justify-content: center;

				@include from-md {
					max-width: 175px;
					max-height: 225px;
				}

				img {
					@include to-md {
						height: 0px;
						width: 0px;
					}
				}
			}
		}
	}

	.year-end-distributions-section {
		@include to-lg {
			padding-top: 28px;
		}
		padding-top: 48px;
	}
}
</style>
